import {
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { ProjectsService } from '../../../../../../core/projects.service';
import { appDataDetails } from '../../../../../../../../shared/core/shared.selectors';
import { projectDetails } from '../../../../../../core/projects.selectors';
import { UploadFileRequest } from '../../../../../../../../shared/core/shared.actions';
import * as moment from 'moment-timezone';

/**
 * Per-test entry side panel — web port of the mobile SampleTestEntryScreen.
 *
 * Opened from CreateOrEditSampleRecordComponent when a user clicks an existing
 * test row in the Tests section. Renders three accordion sections backed by
 * the org's `sample_record_test_general` custom_group plus an agency-view
 * group resolved from the lookup's `extra_attributes.agency_view_context`:
 *
 *   1. Test Information   (sample_record_test_general / "Test Information")
 *      — editable status / dates / priority
 *   2. Sample Information (read-only snapshot of the parent sample's context)
 *      — sourced from `data.parentContext`, NOT a custom_group section
 *   3. Additional         (sample_record_test_general / "Additional")
 *      — editable flags + result value
 *   4. Agency view group  (sample_test_groups[<context>])
 *      — top-level fields only (TODO: nested child/sibling groups)
 *
 * On Save we collect the edits, partition them between top-level test row
 * fields and the agency-view-group `result_values` blob (matches the mobile
 * onTestSaved payload shape exactly), then bubble them up to the parent via
 * `data.onTestSaved(materialTestValue, edits)`. The parent component stashes
 * them in `form.test_overrides[materialTestValue]` and the api's
 * `normalizePayload` expands them onto `sample.tests[]` at submission time.
 *
 * No api calls happen here — the entire interaction is form-state edits.
 */
@Component({
  selector: 'app-sample-test-entry',
  templateUrl: './sample-test-entry.component.html',
  styleUrls: ['./sample-test-entry.component.scss'],
  standalone: false,
})
export class SampleTestEntryComponent implements OnInit, OnDestroy {
  @Input() data: {
    materialTestValue?: string;
    materialTestLabel?: string;
    parentContext?: {
      sampleType?: string;
      sourceId?: string;
      labControlNumber?: string;
      regionalLabNumber?: string;
      contractId?: string;
      labUnit?: string;
    };
    existingEdits?: any;
    /** Viewer mode: every section renders as label/value rows, Save is hidden. */
    readOnly?: boolean;
    onTestSaved?: (materialTestValue: string, edits: any) => void;
  } = {};

  get readOnly(): boolean {
    return !!this.data?.readOnly;
  }

  // Resolved configs
  agencyViewContext: string | null = null;
  // groupConfigs is a flat list — entry 0 is the test_general "wrapper",
  // entry 1 (if present) is the agency-view group. We render sections, not
  // groups directly — groupConfigs is just an intermediate.
  groupConfigs: any[] = [];

  // Child repeating groups declared on the agency-view group's
  // `ui_config.child_groups[]`. Each ref is `{ context, label, row_field,
  // parent_section, repeating, agency_entity_id }`. We resolve the child's
  // custom_group config from `sample_test_groups[ref.context]` so we can
  // render a sub-table per-row with the child's fields.
  childGroups: Array<{
    ref: any;
    config: any;
    rows: FormGroup[];
  }> = [];

  // Section descriptors that drive the template's @for loop. Each entry is
  // either an editable form section (sourced from a custom_group's section)
  // or a read-only "Sample Information" section (sourced from parentContext).
  sections: Array<{
    key: string;
    label: string;
    kind: 'form' | 'readonly';
    fields?: any[];     // for kind='form'
    rows?: { label: string; value: string }[]; // for kind='readonly'
    // Tag the source group so onSave can partition values between the
    // top-level test row and the agency-view result_values blob.
    sourceGroupKey?: 'test_general' | 'agency_view';
    // Child repeating groups whose ui_config.parent_section matches this
    // section's name. Rendered inline beneath the section's scalar fields.
    childGroupIdxs?: number[];
    // The section's slug (e.g. 'riprap_list') — kept on the descriptor so
    // the template can match it against childGroups[].ref.parent_section.
    sectionName?: string;
  }> = [];

  form = new FormGroup<any>({});
  pickListOptions: Record<string, any[]> = {};
  // Per-child-row pick-list options keyed by `${childContext}-${rowIdx}-${fieldName}`.
  childRowPickListOptions: Record<string, any[]> = {};

  // Per-test remarks (mirrors AASHTOWare's "Sample Record Test Remarks").
  // Each row is { remark_type, remark } — stored in test_overrides.remarks
  // so the api can persist + the outbound sync can push to AASHTOWare later.
  remarkTypeOptions: { label: string; value: any }[] = [];
  remarkRows: FormGroup[] = [];

  // Per-test attachments. Uploaded via /attachment first; the saved attachment
  // ids ride along in test_overrides.pictures so the api links them to the
  // sample_record_test row (entity_type='sample_record_test').
  pictures: any[] = [];

  @ViewChild('fileInput', { static: false }) fileInputRef: any;

  expandedSections: Record<string, boolean> = {};
  saving = false;

  private appData: any = null;
  private projectData: any = null;
  private formChangesSubscribed = false;
  private readonly onDestroy = new Subject<void>();

  constructor(
    private store: Store,
    private commonService: CommonService,
    private projectsService: ProjectsService,
    // When opened via MatDialog (the current parent flow), these are present
    // and we close the dialog directly. Both are @Optional so the component
    // also still works if mounted via projectsService.openRightPanel.
    @Optional() private dialogRef: MatDialogRef<SampleTestEntryComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: any,
  ) {
    if (dialogData) this.data = dialogData;
  }

  ngOnInit(): void {
    this.store
      .select(appDataDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((d: any) => {
        this.appData = d;
        this.rebuild();
      });
    this.store
      .select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((p: any) => {
        this.projectData = p;
        if (this.groupConfigs?.length) this.recomputePickListOptions();
      });

    // Receive uploaded picture metadata back from the shared upload effect.
    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: any) => {
        const items = Array.isArray(data) ? data : [data];
        items.forEach((item: any) => {
          if (item?.fileType === 'picture' || item?.fileType === 'document') {
            // Only claim attachments uploaded for THIS form (no `tags`).
            if (!item?.tags?.length) this.pictures.push(this.normalizePicture(item));
          }
        });
        this.publishMediaData();
      });
  }

  /**
   * <app-pictures-section-with-tags> reads pictures from the projectsService
   * mediaData subject, NOT the `[pictures]` input. Republish whenever we
   * mutate `this.pictures` so the shared component shows thumbnails.
   */
  private publishMediaData(): void {
    setTimeout(() => this.projectsService.mediaData.next(this.pictures));
  }

  /**
   * Resolve config + build form/sections. Re-runs whenever appData arrives.
   * Mirrors the mobile screen's groupConfigs / context / form initialisation
   * collapsed into a single pass.
   */
  private rebuild(): void {
    const dr = this.appData?.dailyReportFields || {};
    const general = dr.sample_record_test_general;
    if (!general) {
      this.groupConfigs = [];
      this.sections = [];
      return;
    }

    // Resolve agency-view context from the material_test lookup row.
    const lookups: any[] = this.appData?.lookups?.material_test || [];
    const lookupRow = lookups.find(l => l?.value === this.data?.materialTestValue);
    this.agencyViewContext = lookupRow?.extra_attributes?.agency_view_context || null;

    const generalGroup = {
      key: 'test_general',
      label: 'General',
      ui_config: general.ui_config || {},
      fields: (general.fields || []).slice().sort((a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0)),
    };
    const groups: any[] = [generalGroup];

    if (this.agencyViewContext) {
      const av = dr.sample_test_groups?.[this.agencyViewContext];
      if (av) {
        groups.push({
          key: 'agency_view',
          context: this.agencyViewContext,
          label: av.label || 'Test Results',
          ui_config: av.ui_config || {},
          fields: (av.fields || []).slice().sort((a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0)),
        });
        // Resolve any declared child repeating groups (e.g. ATM 315 →
        // Riprap List). Each child has its own custom_group config in
        // sample_test_groups[ref.context]; we stash {ref, config, rows}
        // so the template can render a sub-table inside the matching
        // parent_section. Sibling groups handled later if we need them.
        const sampleTestGroups = dr.sample_test_groups || {};
        const childRefs: any[] = Array.isArray(av?.ui_config?.child_groups)
          ? av.ui_config.child_groups
          : [];
        this.childGroups = childRefs
          .map((ref: any) => {
            const cfg = sampleTestGroups?.[ref?.context];
            if (!cfg) return null;
            return {
              ref,
              config: {
                ...cfg,
                fields: (cfg.fields || [])
                  .slice()
                  .sort((a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0)),
              },
              rows: [] as FormGroup[],
            };
          })
          .filter((g: any) => !!g);
      } else {
        this.childGroups = [];
      }
    } else {
      this.childGroups = [];
    }
    this.groupConfigs = groups;

    this.buildForm();
    this.buildChildRows();
    this.buildSections();
    this.buildRemarks();
    this.recomputePickListOptions();
    if (this.readOnly) {
      this.form.disable({ emitEvent: false });
      this.childGroups.forEach(cg => cg.rows.forEach(r => r.disable({ emitEvent: false })));
      this.remarkRows.forEach(r => r.disable({ emitEvent: false }));
    }

    if (!this.formChangesSubscribed) {
      this.formChangesSubscribed = true;
      this.form.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => this.recomputePickListOptions());
    }
  }

  /**
   * Build form controls for every editable field across all groups. Hydrates
   * from `data.existingEdits` — top-level keys go directly, agency-view keys
   * come out of `result_values` (the same shape the mobile screen produces).
   * Reserved keys `__children__` / `__siblings__` on `result_values` are
   * unpacked separately by `buildChildRows()` and `buildSiblingRows()`.
   */
  private buildForm(): void {
    Object.keys(this.form.controls).forEach(k => this.form.removeControl(k, { emitEvent: false }));

    const edits = this.data?.existingEdits || {};
    const resultValues = edits.result_values || {};

    this.groupConfigs.forEach(group => {
      (group.fields || []).forEach((f: any) => {
        if (this.form.contains(f.name)) return;
        // Sample Information section is rendered read-only from parentContext;
        // skip building form controls for those fields so they don't pollute
        // the saved payload.
        if (group.key === 'test_general' && f?.ui_config?.section_name === 'Sample Information') {
          return;
        }
        let seed: any;
        if (group.key === 'agency_view') {
          seed = resultValues[f.name];
        } else {
          seed = edits[f.name];
        }
        // The `remarks` and `pictures` keys on `existingEdits` carry the
        // structured per-test remarks array and attachment list — they hydrate
        // their own dedicated UIs (`remarkRows` / `pictures`), NOT the
        // sample_record_test_general "remarks" / "pictures" text fields. If we
        // let those array seeds reach a plain text-area form control it
        // renders the literal `[object Object]` string. Reset to empty so the
        // dedicated section is the only place those values surface.
        if (group.key === 'test_general' && (f.name === 'remarks' || f.name === 'pictures') && Array.isArray(seed)) {
          seed = null;
        }
        if (seed === undefined || seed === null) {
          if (f?.ui_config?.multi_select) seed = [];
          else seed = null;
        }
        this.form.addControl(f.name, new FormControl(seed), { emitEvent: false });
      });
    });
  }

  /**
   * Hydrate `childGroups[i].rows` from the saved
   * `result_values.__children__[<context>]` array. Each saved row is a flat
   * object of field-name → value; we build a FormGroup mirroring the child's
   * field config so the template's repeating-row UI binds directly. Empty
   * arrays leave `rows = []` — the user can use "+ Add row" to add entries.
   */
  private buildChildRows(): void {
    if (!this.childGroups.length) return;
    const edits = this.data?.existingEdits || {};
    const resultValues = edits.result_values || {};
    const savedChildren = resultValues.__children__ || {};
    this.childGroups.forEach(cg => {
      const seedRows: any[] = Array.isArray(savedChildren[cg.ref.context])
        ? savedChildren[cg.ref.context]
        : [];
      cg.rows = seedRows.map(seed => this.buildChildRowFormGroup(cg.config.fields || [], seed));
    });
  }

  /**
   * Build a FormGroup for one repeating child row. Mirrors the parent
   * sample-record form's `buildRowFormGroup` shape so the same dropdown /
   * date / text components work without modification.
   */
  private buildChildRowFormGroup(fields: any[], seed: any = {}): FormGroup {
    const controls: Record<string, FormControl> = {};
    (fields || []).forEach((f: any) => {
      let v = seed?.[f.name];
      if (v === undefined || v === null) v = f?.ui_config?.multi_select ? [] : null;
      controls[f.name] = new FormControl(v);
    });
    const fg = new FormGroup(controls);
    // Recompute pick-list options if a parent-keyed field in this row
    // changes — keeps filter_by-style child dropdowns honest.
    fg.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => this.recomputePickListOptions());
    return fg;
  }

  addChildRow(childIdx: number): void {
    const cg = this.childGroups[childIdx];
    if (!cg) return;
    cg.rows = [...cg.rows, this.buildChildRowFormGroup(cg.config.fields || [], {})];
    this.recomputePickListOptions();
  }

  removeChildRow(childIdx: number, rowIdx: number): void {
    const cg = this.childGroups[childIdx];
    if (!cg) return;
    cg.rows = cg.rows.filter((_, i) => i !== rowIdx);
    this.recomputePickListOptions();
  }

  /** Build the ordered section list driving the template. */
  private buildSections(): void {
    const sections: typeof this.sections = [];

    const generalGroup = this.groupConfigs.find(g => g.key === 'test_general');
    const agencyGroup = this.groupConfigs.find(g => g.key === 'agency_view');

    if (generalGroup) {
      const sectionOrder = (generalGroup.ui_config?.sections || []) as Array<{ name: string; label?: string }>;
      // Bucket fields by section_name so we can render Test Information /
      // Additional independently. Sample Information is special-cased below.
      const buckets: Record<string, any[]> = {};
      (generalGroup.fields || []).forEach((f: any) => {
        const name = f?.ui_config?.section_name || '_unsectioned';
        if (!buckets[name]) buckets[name] = [];
        buckets[name].push(f);
      });

      const orderedNames = sectionOrder.length
        ? sectionOrder.map(s => s.name)
        : Object.keys(buckets);

      orderedNames.forEach(name => {
        const meta = sectionOrder.find(s => s.name === name);
        const label = meta?.label || name;
        // The DB stores section.name as a slug ('sample_info', 'test_info',
        // 'additional'); only the displayable label is human-readable.
        // Compare against the slug, not the label.
        if (name === 'sample_info') {
          sections.push({
            key: `sample_info`,
            label,
            kind: 'readonly',
            rows: this.buildSampleInfoRows(),
          });
        } else if (buckets[name]?.length) {
          sections.push({
            key: `general-${name}`,
            label,
            kind: 'form',
            fields: buckets[name],
            sourceGroupKey: 'test_general',
          });
        }
      });
    }

    if (agencyGroup) {
      // Apply the same section-bucketing logic mobile uses (groupFieldsBySection)
      // — fields are tagged with ui_config.section_name and the group's
      // ui_config.sections[] declares display labels + order. ATM 213 v002, for
      // example, has 7 sections (test_result_info, standard_density,
      // density_determination, moisture_content, gradation_oversize, compaction,
      // fields). Without this, the entire agency view collapses into a single
      // sub-accordion that doesn't match the mobile UI.
      const avSectionOrder = (agencyGroup.ui_config?.sections || []) as Array<{ name: string; label?: string }>;
      const avBuckets: Record<string, any[]> = {};
      (agencyGroup.fields || []).forEach((f: any) => {
        const name = f?.ui_config?.section_name || '_unsectioned';
        if (!avBuckets[name]) avBuckets[name] = [];
        avBuckets[name].push(f);
      });
      const avOrderedNames = avSectionOrder.length
        ? avSectionOrder.map(s => s.name)
        : Object.keys(avBuckets);

      const matchedChildren = new Set<number>();

      avOrderedNames.forEach(name => {
        const sectionChildIdxs: number[] = this.childGroups
          .map((cg, idx) => (cg.ref?.parent_section === name ? idx : -1))
          .filter(idx => idx >= 0);
        sectionChildIdxs.forEach(idx => matchedChildren.add(idx));
        const hasFields = !!avBuckets[name]?.length;
        // Keep the section even if it has no scalar fields when a child
        // group is going to render inside it (e.g. ATM 315's "Riprap List"
        // section is just the host for the Riprap List sub-table).
        if (!hasFields && sectionChildIdxs.length === 0) return;

        const meta = avSectionOrder.find(s => s.name === name);
        // Section with empty label ("") renders without a sub-header — fields
        // flow inline under the group.
        const label = meta?.label || (meta && meta.label === '' ? '' : name);
        sections.push({
          key: `agency_view-${agencyGroup.context}-${name}`,
          label: label || agencyGroup.label || 'Test Results',
          kind: 'form',
          fields: avBuckets[name] || [],
          sourceGroupKey: 'agency_view',
          sectionName: name,
          childGroupIdxs: sectionChildIdxs,
        });
      });

      // Child groups with no matching parent_section (or whose parent_section
      // wasn't in the agency view's sections array) fall through and render
      // as their own sections below the matched ones.
      this.childGroups.forEach((cg, idx) => {
        if (matchedChildren.has(idx)) return;
        sections.push({
          key: `agency_view-${agencyGroup.context}-child-${cg.ref.context}`,
          label: cg.ref?.label || cg.config?.label || cg.ref.context,
          kind: 'form',
          fields: [],
          sourceGroupKey: 'agency_view',
          childGroupIdxs: [idx],
        });
      });

      // TODO(sibling groups): sibling_groups (e.g. ATM 309 RollList) follow
      // the same pattern as children but pack into __siblings__ on save.
      // Deferred — current scope is child_groups only.
    }

    this.sections = sections;

    // Default expansion: every section starts open (matches mobile, which
    // keeps section accordions expanded by default and only collapses on tap).
    sections.forEach(s => {
      if (this.expandedSections[s.key] === undefined) this.expandedSections[s.key] = true;
    });
  }

  /**
   * Per-test remarks UI — reuses the org's `sample_record_remarks` group's
   * `remark_type` field config so the picker options match the parent sample
   * remarks dropdown. Hydrates rows from existingEdits.remarks.
   */
  private buildRemarks(): void {
    const dr = this.appData?.dailyReportFields || {};
    const remarksGroup = dr.sample_record_remarks;
    const typeField = (remarksGroup?.fields || [])
      .find((f: any) => f?.name === 'remark_type');
    if (typeField) {
      this.remarkTypeOptions = this.resolvePickListOptions(typeField, {});
    } else {
      this.remarkTypeOptions = [];
    }

    const seedRows = Array.isArray(this.data?.existingEdits?.remarks)
      ? this.data.existingEdits.remarks
      : [];
    this.remarkRows = seedRows.map((r: any) => new FormGroup({
      remark_type: new FormControl(r?.remark_type ?? null),
      remark: new FormControl(r?.remark ?? ''),
    }));

    // Hydrate per-test attachments. The api's getFullRecord stuffs them on
    // each test row's `pictures` array; CreateOrEditSampleRecord forwards
    // that into existingEdits.pictures when opening the popup.
    const seedPics = Array.isArray(this.data?.existingEdits?.pictures)
      ? this.data.existingEdits.pictures
      : [];
    this.pictures = seedPics.map((p: any) => this.normalizePicture(p));
    this.publishMediaData();
  }

  addRemarkRow(): void {
    this.remarkRows.push(new FormGroup({
      remark_type: new FormControl(null),
      remark: new FormControl(''),
    }));
  }

  removeRemarkRow(idx: number): void {
    this.remarkRows.splice(idx, 1);
  }

  removePicture(pic: any): void {
    this.pictures = this.pictures.filter(p => p?.id !== pic?.id);
    this.publishMediaData();
  }

  openFilePicker(): void {
    if (this.readOnly) return;
    this.fileInputRef?.nativeElement?.click();
  }

  onFileSelected(event: any): void {
    const files: FileList = event.target.files;
    const formData = new FormData();
    let hasFile = false;
    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      if (file) {
        formData.append('inputFiles', file);
        hasFile = true;
      }
    }
    if (hasFile) this.store.dispatch(UploadFileRequest({ payload: formData }));
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }
  }

  /** Mirrors CreateOrEditSampleRecordComponent.normalizePicture. */
  private normalizePicture(p: any): any {
    if (!p) return p;
    if (p.fileType) return p;
    const mime = p.mime || '';
    let fileType: string | undefined;
    if (mime.startsWith('image/')) fileType = 'picture';
    else if (mime.startsWith('video/')) fileType = 'video';
    else if (mime.startsWith('application/') || mime.startsWith('text/')) fileType = 'document';
    return fileType ? { ...p, fileType } : { ...p, fileType: 'picture' };
  }

  /** Build the read-only label/value rows from parent context. */
  private buildSampleInfoRows(): { label: string; value: string }[] {
    const ctx = this.data?.parentContext || {};
    const dash = (v: any) => (v === undefined || v === null || v === '' ? '-' : String(v));
    return [
      { label: 'Sample Type', value: dash(ctx.sampleType) },
      { label: 'Source', value: dash(ctx.sourceId) },
      { label: 'Lab Control Number', value: dash(ctx.labControlNumber) },
      { label: 'Regional Lab Number', value: dash(ctx.regionalLabNumber) },
      { label: 'Contract', value: dash(ctx.contractId) },
      { label: 'Lab Unit', value: dash(ctx.labUnit) },
    ];
  }

  // ----- Pick-list resolution (trimmed copy of CreateOrEditSampleRecord) ----

  private recomputePickListOptions(): void {
    const formValues = this.form.getRawValue();
    this.pickListOptions = {};
    this.childRowPickListOptions = {};
    this.groupConfigs.forEach(group => {
      (group.fields || []).forEach((f: any) => {
        if (!this.fieldHasPickList(f)) return;
        const opts = this.resolvePickListOptions(f, formValues);
        this.pickListOptions[f.name] = this.ensureValueInOptions(
          opts,
          formValues?.[f.name],
          !!f?.ui_config?.multi_select,
        );
      });
    });
    // Per-row pick-lists for each child repeating group.
    this.childGroups.forEach(cg => {
      cg.rows.forEach((row, idx) => {
        const rowValues = row.getRawValue();
        (cg.config?.fields || []).forEach((f: any) => {
          if (!this.fieldHasPickList(f)) return;
          const opts = this.resolvePickListOptions(f, rowValues);
          this.childRowPickListOptions[`${cg.ref.context}-${idx}-${f.name}`] = this.ensureValueInOptions(
            opts,
            rowValues?.[f.name],
            !!f?.ui_config?.multi_select,
          );
        });
      });
    });
  }

  /** Per-child-row chip label resolver — mirrors `chipLabel` for child rows. */
  childChipLabel(childContext: string, rowIdx: number, field: any, value: any): string {
    const opts = this.childRowPickListOptions[`${childContext}-${rowIdx}-${field.name}`] || [];
    const match = opts.find((o: any) => o.value === value || String(o.value) === String(value));
    return match?.label ?? String(value);
  }

  /**
   * Mirror CreateOrEditSampleRecord.ensureValueInOptions — synthesize a
   * placeholder option when the hydrated form value isn't in the resolved
   * options pool (config reshuffled after save, lookup still loading) so
   * mat-select displays a label instead of rendering empty.
   */
  private ensureValueInOptions(opts: any[], value: any, isMulti: boolean): any[] {
    const out = Array.isArray(opts) ? [...opts] : [];
    const has = (v: any) => out.some(o => o?.value === v || String(o?.value) === String(v));
    const add = (v: any) => {
      if (v === undefined || v === null || v === '') return;
      if (!has(v)) out.unshift({ label: String(v), value: v });
    };
    if (isMulti && Array.isArray(value)) value.forEach(add);
    else add(value);
    return out;
  }

  private resolvePickListOptions(field: any, formValues: any): any[] {
    const inline = field?.ui_config?.pick_list_values;
    if (Array.isArray(inline) && inline.length > 0) {
      return inline.map((v: any) =>
        typeof v === 'string'
          ? { label: v, value: v }
          : { label: v.label || v.name || v.value, value: v.value ?? v.name },
      );
    }

    let pickListType: string | undefined = field?.ui_config?.pick_list?.split('.')?.[0];
    const pickListBy = field?.ui_config?.pick_list_by;
    if (!pickListType && pickListBy?.field && pickListBy?.values) {
      const parentValue = formValues?.[pickListBy.field];
      const mapped = parentValue ? pickListBy.values[parentValue] : null;
      if (mapped) pickListType = mapped.split('.')?.[0];
    }
    if (!pickListType) return [];

    const projectLookups = this.projectData?.lookups?.[pickListType] || [];
    const appLookups = this.appData?.lookups?.[pickListType] || [];
    let pool = projectLookups.length > 0 ? projectLookups : appLookups;

    const filterBy = field?.ui_config?.filter_by;
    if (filterBy?.field && filterBy?.lookup_attribute) {
      const parentValue = formValues?.[filterBy.field];
      if (parentValue !== undefined && parentValue !== null && parentValue !== '') {
        pool = pool.filter(
          (item: any) => item?.extra_attributes?.[filterBy.lookup_attribute] === parentValue,
        );
      }
    }

    return pool.map((item: any) => ({
      label: item.name || item.value || String(item),
      value: item.value ?? item.name,
    }));
  }

  // ---- Field-type predicates (mirrors CreateOrEditSampleRecord) ------------

  fieldHasPickList(field: any): boolean {
    return Array.isArray(field?.ui_config?.pick_list_values)
      ? field.ui_config.pick_list_values.length > 0
      : !!(field?.ui_config?.pick_list || field?.ui_config?.pick_list_by);
  }
  isMultiSelect(field: any): boolean { return !!field?.ui_config?.multi_select; }
  isDate(field: any): boolean { return field?.type === 'date'; }
  isMultiline(field: any): boolean { return !!field?.ui_config?.multiline; }
  isDisplayOnly(field: any): boolean { return !!field?.ui_config?.display_only; }

  displayValueFor(field: any): string {
    return this.formatValue(field, this.form.get(field?.name)?.value, this.pickListOptions[field?.name]);
  }

  /** Viewer-mode value for a child-row field (per-row pick-list options). */
  childDisplayValueFor(childContext: string, rowIdx: number, field: any, row: FormGroup): string {
    return this.formatValue(
      field,
      row?.get(field?.name)?.value,
      this.childRowPickListOptions[`${childContext}-${rowIdx}-${field?.name}`],
    );
  }

  /** Viewer-mode label for a remark row's type. */
  remarkTypeLabel(row: FormGroup): string {
    const v = row?.get('remark_type')?.value;
    if (v === undefined || v === null || v === '') return '-';
    const match = this.remarkTypeOptions.find(o => o.value === v || String(o.value) === String(v));
    return match?.label ?? String(v);
  }

  /**
   * Pick lists resolve to their option label, multi-selects join their
   * labels, dates print as "Sep 22, 2026"; empty prints "-".
   */
  private formatValue(field: any, value: any, opts: any[] | undefined): string {
    if (value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length)) {
      return '-';
    }
    const labelFor = (v: any) => {
      const match = (opts || []).find(o => o.value === v || String(o.value) === String(v));
      return match?.label ?? String(v);
    };
    if (Array.isArray(value)) return value.map(labelFor).join(', ');
    if (this.fieldHasPickList(field)) return labelFor(value);
    if (this.isDate(field)) {
      const parsed = moment(value, [moment.ISO_8601, 'YYYY-MM-DD', 'MM/DD/YYYY'], true);
      return parsed.isValid() ? parsed.format('MMM D, YYYY') : String(value);
    }
    return String(value);
  }

  /** chip-label resolver used by the multi-select rendering. */
  chipLabel(field: any, value: any): string {
    const opts = this.pickListOptions[field.name] || [];
    const match = opts.find(o => o.value === value || String(o.value) === String(value));
    return match?.label ?? String(value);
  }

  // ---- Section accordion ---------------------------------------------------

  toggleSection(key: string): void {
    this.expandedSections[key] = !this.expandedSections[key];
  }
  isSectionExpanded(key: string): boolean {
    return this.expandedSections[key] !== false;
  }

  // ---- Save ----------------------------------------------------------------

  /**
   * Mirror the mobile onSave partitioning: editable General fields go to the
   * top of the edits object; everything from the agency-view group nests
   * under `result_values`. The api side reads those keys directly.
   */
  save(): void {
    if (this.saving || this.readOnly) return;
    if (typeof this.data?.onTestSaved !== 'function') {
      this.commonService.notification('Internal error: missing save handler', 'error');
      return;
    }
    if (!this.data?.materialTestValue) {
      this.commonService.notification('Cannot save — material test value is missing', 'error');
      return;
    }

    this.saving = true;
    const editable: any = {};
    const resultValues: any = {};

    this.groupConfigs.forEach(group => {
      (group.fields || []).forEach((f: any) => {
        // Read-only sample-information fields never had a control built.
        if (!this.form.contains(f.name)) return;
        const v = this.form.get(f.name)?.value;
        if (group.key === 'agency_view') {
          // Carry every agency-view field, including display-only/computed —
          // mobile's onSave does the same so AASHTOWare receives a complete
          // DataXml on push.
          resultValues[f.name] = v;
        } else if (!this.isDisplayOnly(f)) {
          editable[f.name] = v;
        }
      });
    });

    // Pack child repeating-group rows into the reserved __children__ key
    // under result_values (matches the mobile shape exactly so the api's
    // outbound sync can POST each row as its own AgencyEntityInstance).
    if (this.childGroups.length > 0) {
      const childRowsByContext: Record<string, any[]> = {};
      this.childGroups.forEach(cg => {
        const rowValues = cg.rows.map(g => g.getRawValue());
        if (rowValues.length > 0) {
          childRowsByContext[cg.ref.context] = rowValues;
        }
      });
      if (Object.keys(childRowsByContext).length > 0) {
        resultValues.__children__ = childRowsByContext;
      }
    }

    if (Object.keys(resultValues).length > 0) {
      editable.result_values = resultValues;
    }

    // Per-test remarks — drop empty rows (no type AND no text).
    const remarks = this.remarkRows
      .map(g => g.getRawValue())
      .filter(r => r?.remark_type || (r?.remark && String(r.remark).trim()));
    editable.remarks = remarks;

    // Per-test attachments — pass attachment ids through; api links them.
    editable.pictures = this.pictures.map(p => p?.id).filter(Boolean);

    this.data.onTestSaved(this.data.materialTestValue, editable);
    this.saving = false;
    this.close();
  }

  close(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.projectsService.closeRightPanel.next();
    }
  }

  trackByName(_idx: number, item: any): string {
    return item?.name || item?.uuid || String(_idx);
  }
  trackByValue(_idx: number, value: any): any {
    return value;
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
